import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { finalize, forkJoin, Observable, of, Subject, Subscription, switchMap } from "rxjs";
import { animateElementValue } from "src/app/animations/counter-animation";
import LocalizationConstants from "src/app/constants/localization-constants";
import RouteNameConstants from "src/app/constants/route-name-constants";
import { ScheduleDueType, StatusTypes } from "src/app/models/Enums";
import { ToolFilterCategory } from "src/app/models/Filters/ToolFilterCategory";
import { OverviewCounterItem } from "src/app/models/OverviewCounterItem";
import { SearchResult } from "src/app/models/SearchResult";
import { TechCoverContact } from "src/app/models/TechCoverContact";
import { Tool } from "src/app/models/Tool";
import { ColumnFilter, ToolFilter } from "src/app/models/ToolFilter";
import { CompanyService } from "src/app/services/company.service";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { TechCoverContactService } from "src/app/services/tech-cover-contact.service";
import { TechCoverTicketService } from "src/app/services/tech-cover-ticket.service";
import { TechCoverVisibilityService } from "src/app/services/tech-cover-visibility.service";
import { ToolFilterService } from "src/app/services/tool-filter.service";
import { getFamFromCompany } from "src/app/utils/company.utils";

import { ToolService } from "../../services/tool.service";
import { ScheduleDueTypeName, ViewType } from "./../../models/Enums";
import { AuthService } from "./../../services/auth.service";
import { PoBasketService } from "./../../services/po-basket.service";
import { RaBasketService } from "./../../services/ra-basket.service";

@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.scss"],
  standalone: false,
})
export class HomeComponent implements OnInit, OnDestroy {
  readonly routeConstants = RouteNameConstants;
  private _subscriptions$: Subscription[] = [];
  toolsTotal: number = 0;
  localizationConstants = LocalizationConstants;
  techCoverContact: TechCoverContact;
  supportTicketsCount = 0;
  supportTicketsLineWidth: string;

  readonly toolsTotalId = "toolsTotal";
  readonly warningsTotalId = "warningsTotal";

  toolStatusItems: OverviewCounterItem[] = [];
  serviceWarningsItems: OverviewCounterItem[] = [];
  warningsTotal: number = 0;
  isTechCoverVisible = this.techCoverVisibilityService.isTechCoverComputed;
  private readonly _loadingHomeData$ = new Subject<void>();

  constructor(
    public authService: AuthService,
    private toolsService: ToolService,
    public companyService: CompanyService,
    private router: Router,
    public raBasket: RaBasketService,
    public poBasket: PoBasketService,
    private globalOverlayService: GlobalOverlayService,
    private toolFilterService: ToolFilterService,
    private techCoverContactService: TechCoverContactService,
    private techCoverService: TechCoverTicketService,
    private techCoverVisibilityService: TechCoverVisibilityService,
  ) {}

  ngOnInit() {
    this._subscriptions$.push(this.onCompanyChange());
    this._subscriptions$.push(this.onDataLoad());

    this._loadingHomeData$.next();

    this.setToolStatusItems();
    this.setServiceWarningItems();
  }

  trackByFn(i: number, item: OverviewCounterItem): string {
    return item.type;
  }

  private onCompanyChange(): Subscription {
    return this.companyService.companyChange.subscribe(() => {
      this._loadingHomeData$.next();
    });
  }

  private onDataLoad(): Subscription {
    // to handle the auto-resetting of region for multi-region users
    // switchMap is used to cancel the previous HTTP request and take only the
    // the last request to the server. This is to address problem with region
    // auto set in home component. E.g. when auto-resetting the region from APAC to EMEA for the user located in EMEA,
    // home component makes 2 request for APAC first and then to EMEA. But because server call to APAC takes longer than calling EMEA db,
    // the value from APAC region being last input and displayed to the user (since it was the lat to finish).
    return this._loadingHomeData$
      .pipe(switchMap(() => this.loadHomeDataAsync()))
      .subscribe((res) => {
        const [statusResult, warningResult, techCoverContact, openSupportTicketsCount] = res;

        this.loadStatusCounts(statusResult);
        this.loadWarningCounts(warningResult);
        this.techCoverContact = techCoverContact;
        this.setSoftwareSupportCount(openSupportTicketsCount);
      });
  }

  private getTechCoverContact(): Observable<TechCoverContact> {
    return this.authService.user?.allowTechCover
      ? this.techCoverContactService.getContact(
          getFamFromCompany(this.companyService.selectedCompany.name),
        )
      : of(null);
  }

  private setSoftwareSupportCount(openTicketsCount: number): void {
    this.supportTicketsCount = openTicketsCount ?? 0;
    this.supportTicketsLineWidth = this.getTicketsLineWidthPercent(this.supportTicketsCount);
  }

  /**
   * Calculate the width of the tickets line based on the number of open tickets
   * If the number of open tickets is greater than 100, the line will be 100% width
   */
  private getTicketsLineWidthPercent(openTicketsCount: number): string {
    const maxTicketsCountForFullLine = 100;
    return openTicketsCount > maxTicketsCountForFullLine
      ? "100%"
      : (openTicketsCount / maxTicketsCountForFullLine) * 100 + "%";
  }

  private getOpenSupportTicketsCount(): Observable<number> {
    return this.authService.user?.allowTechCover
      ? this.techCoverService.getOpenSupportTicketsCountAsync(
          this.companyService.selectedCompany.name,
        )
      : of(0);
  }

  /** Load counters from aggregations */
  private loadHomeDataAsync(): Observable<
    [SearchResult<Tool>, SearchResult<Tool>, TechCoverContact, number]
  > {
    // don't load the aggregation if the company is not selected
    if (!this.companyService.selectedCompany) {
      return of();
    }

    this.globalOverlayService.show();
    const statusFilter = this.toolFilterService.create();
    const dueFilter = JSON.parse(JSON.stringify(statusFilter));
    dueFilter.serviceDueMonths = 0;
    dueFilter.calibrationDueMonths = 0;

    return forkJoin([
      this.toolsService.getByFilter(statusFilter, true),
      this.toolsService.getByFilter(dueFilter, true),
      this.getTechCoverContact(),
      this.getOpenSupportTicketsCount(),
    ]).pipe(finalize(() => this.globalOverlayService.close()));
  }

  private loadStatusCounts(result: SearchResult<Tool>) {
    const aggregations = result.aggregations;
    const statusAggregations = new ToolFilterCategory(
      aggregations.find((a) => a.id === "eventStatus"),
    );

    this.toolsTotal = result["totalCount"];
    this.setToolStatusItems(statusAggregations);
    this.animateCounters();
  }
  private animateCounters() {
    const totalsIds = [this.toolsTotalId, this.warningsTotalId];
    const ids: string[] = [];

    ids.push(...totalsIds);
    this.toolStatusItems.map((a) => a.type).forEach((b) => !ids.includes(b) && ids.push(b));
    this.serviceWarningsItems.map((a) => a.type).forEach((b) => !ids.includes(b) && ids.push(b));

    setTimeout(() => {
      ids.forEach((id) => {
        const counter = document.getElementById(id);
        const item =
          this.toolStatusItems.find((item) => item.type === id) ||
          this.serviceWarningsItems.find((item) => item.type === id);

        const count = item
          ? item.count
          : id === this.toolsTotalId
            ? this.toolsTotal
            : id === this.warningsTotalId
              ? this.warningsTotal
              : 0;

        animateElementValue(counter, 0, count, 500);
      });
    });
  }

  private loadWarningCounts(result: SearchResult<Tool>) {
    const aggregations = result.aggregations;
    const serviceAggregations = new ToolFilterCategory(
      aggregations.find((a) => a.id === "serviceDue"),
    );

    this.warningsTotal = serviceAggregations.options.reduce<number>(
      (prev, curr) => prev + curr.count,
      0,
    );
    this.setServiceWarningItems(serviceAggregations);
  }

  setToolStatusItems(statusAggregations?: ToolFilterCategory) {
    this.toolStatusItems = this.toolsService.statuses.map((status) => {
      const statusItem = statusAggregations?.options.find((a) => a.key === status.value);
      return new OverviewCounterItem(
        statusItem ? statusItem.count : 0,
        status.value,
        status.title,
        status.value === StatusTypes.AwaitingPO,
      );
    });
  }

  setServiceWarningItems(serviceAggregations?: ToolFilterCategory) {
    this.serviceWarningsItems = [
      ScheduleDueTypeName.ServiceDue,
      ScheduleDueTypeName.CalibrationDue,
    ].map((warning) => {
      const warningItem = serviceAggregations?.options.find((a) => a.value === warning);
      return new OverviewCounterItem(
        warningItem ? warningItem.count : 0,
        warning,
        warning === ScheduleDueTypeName.ServiceDue
          ? LocalizationConstants.OVERVIEW_MAINTENANCE_DUE
          : LocalizationConstants.OVERVIEW_CALIBRATION_DUE,
        true,
      );
    });
  }

  isLoggedIn() {
    return this.authService.isLoggedIn;
  }

  onOverviewLinkClick(item: OverviewCounterItem) {
    const filter = this.getFilter(item);

    this.toolFilterService.set(filter);
    this.router.navigate([RouteNameConstants.TOOLS]);
  }

  getFilter(item: OverviewCounterItem): ToolFilter {
    const filter = this.toolFilterService.create();
    filter.gridViewType = ViewType.ToolsAll;
    const isStatusItem = this.toolStatusItems.includes(item);

    return isStatusItem
      ? this.getStatusFilter(item.type, filter)
      : this.getServiceFilter(item.type, filter);
  }

  getStatusFilter(type: string, filter: ToolFilter): ToolFilter {
    const columnFilter = new ColumnFilter("eventStatus");
    columnFilter.values = [type];
    filter.columnFilters = [columnFilter];
    return filter;
  }

  getServiceFilter(type: string, filter: ToolFilter): ToolFilter {
    filter.serviceDueFilters = [ScheduleDueType[type]];
    const isServiceDue = type === ScheduleDueTypeName.ServiceDue;

    if (isServiceDue) {
      filter.serviceDueMonths = 0;
    } else {
      filter.calibrationDueMonths = 0;
    }

    return filter;
  }

  ngOnDestroy(): void {
    this._subscriptions$.forEach((a) => a.unsubscribe());
  }
}
